import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, MoreHorizontal, Trash2, Edit, Star, Users } from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Skeleton } from '../ui/skeleton'
import { CountryDisplay } from '../ui/country-select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { ClientForm } from './ClientForm'
import { toast } from '../../hooks/useToast'
import { getApiError } from '../../lib/api'
import {
  useClients,
  useCreateClient,
  useUpdateClient,
  useDeleteClient,
  useSetDefaultClient,
} from '../../hooks/useClients'
import { Client, ClientCreate, ClientUpdate } from '../../types/client'

export function ClientList() {
  const { t } = useTranslation()
  const { data: clients = [], isLoading } = useClients()
  const createMutation = useCreateClient()
  const updateMutation = useUpdateClient()
  const deleteMutation = useDeleteClient()
  const setDefaultMutation = useSetDefaultClient()

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingClient, setEditingClient] = useState<Client | null>(null)

  const handleCreate = async (data: ClientCreate | ClientUpdate) => {
    try {
      await createMutation.mutateAsync(data as ClientCreate)
      toast({
        title: t('settings.clients.created'),
        description: t('settings.clients.createdDesc'),
      })
      setIsFormOpen(false)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleUpdate = async (data: ClientCreate | ClientUpdate) => {
    if (!editingClient) return
    try {
      await updateMutation.mutateAsync({
        id: editingClient.id,
        data: data as ClientUpdate,
      })
      toast({
        title: t('settings.clients.updated'),
        description: t('settings.clients.updatedDesc'),
      })
      setEditingClient(null)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('settings.clients.confirmDelete'))) return
    try {
      await deleteMutation.mutateAsync(id)
      toast({
        title: t('settings.clients.deleted'),
        description: t('settings.clients.deletedDesc'),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleSetDefault = async (id: number) => {
    try {
      await setDefaultMutation.mutateAsync(id)
      toast({
        title: t('settings.clients.defaultSet'),
        description: t('settings.clients.defaultSetDesc'),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-60" />
            </CardHeader>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">{t('settings.clients.title')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('settings.clients.description')}
          </p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('settings.clients.add')}
        </Button>
      </div>

      {clients.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10">
            <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">{t('settings.clients.empty')}</p>
            <Button className="mt-4" onClick={() => setIsFormOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('settings.clients.addFirst')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {clients.map((client) => (
            <Card key={client.id}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div className="space-y-1">
                  <CardTitle className="text-base flex items-center gap-2">
                    {client.name}
                    {client.isDefault && (
                      <Badge variant="secondary">
                        <Star className="mr-1 h-3 w-3" />
                        {t('settings.default')}
                      </Badge>
                    )}
                  </CardTitle>
                  {client.taxId && (
                    <CardDescription>{client.taxId}</CardDescription>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditingClient(client)}>
                      <Edit className="mr-2 h-4 w-4" />
                      {t('common.edit')}
                    </DropdownMenuItem>
                    {!client.isDefault && (
                      <DropdownMenuItem onClick={() => handleSetDefault(client.id)}>
                        <Star className="mr-2 h-4 w-4" />
                        {t('settings.clients.setAsDefault')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => handleDelete(client.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('common.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {(client.street || client.city || client.postalCode || client.countryCode) && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">
                        {t('settings.clients.address')}:{' '}
                      </span>
                      <span>
                        {client.street}
                        {client.city && `, ${client.city}`}
                        {client.postalCode && ` ${client.postalCode}`}
                        {client.countryCode && (
                          <>, <CountryDisplay alpha3={client.countryCode} /></>
                        )}
                      </span>
                    </div>
                  )}
                  {client.email && (
                    <div>
                      <span className="text-muted-foreground">
                        {t('settings.clients.email')}:{' '}
                      </span>
                      <span>{client.email}</span>
                    </div>
                  )}
                  {client.phone && (
                    <div>
                      <span className="text-muted-foreground">
                        {t('settings.clients.phone')}:{' '}
                      </span>
                      <span>{client.phone}</span>
                    </div>
                  )}
                  {client.notes && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">
                        {t('settings.clients.notes')}:{' '}
                      </span>
                      <span className="text-xs">{client.notes}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ClientForm
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      <ClientForm
        open={!!editingClient}
        onClose={() => setEditingClient(null)}
        onSubmit={handleUpdate}
        client={editingClient}
        isLoading={updateMutation.isPending}
      />
    </div>
  )
}
